import { Modal } from '@dbt-labs/sourdough';

import { WarehouseEntityType } from '@/__generated__/graphql';
import { ProjectEnvironmentSelector } from '@/components/ProjectEnvironmentSelector';
import { useHandoffEventAccount } from '@/hooks/telemetry/handoffs';
import { useProjectEnvironmentSelector } from '@/hooks/useProjectEnvironmentSelector';
import { useCanvasDWHHandoff } from '@/hooks/visual-editor/handoffs';
import { CanvasDataWarehouseObject, CanvasHandoff } from '@/hooks/visual-editor/types';
import { toHandoffAssetDataWarehouseType } from '@/util/handoffs';

type CanvasHandoffModalProps = {
  asset: CanvasDataWarehouseObject;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
};

export const CanvasHandoffModal = ({
  asset,
  isOpen,
  setIsOpen,
}: CanvasHandoffModalProps) => {
  const { sendHandoffEventAccount } = useHandoffEventAccount();
  const isDataWarehouseObject = 'type' in asset && 'warehouseType' in asset;

  const {
    selectedProjectId,
    selectedEnvironmentId,
    projectOptions,
    environmentOptions,
    isLoading,
    isLoadingEnvironments,
    selectedProjectOption,
    selectedEnvironmentOption,
    handleProjectChange,
    handleEnvironmentChange,
  } = useProjectEnvironmentSelector({
    isOpen,
    showEnvironmentSelector: isDataWarehouseObject,
  });

  const dwhHandoff = useCanvasDWHHandoff(
    isDataWarehouseObject ? asset.type : WarehouseEntityType.Table,
    asset.id,
    isDataWarehouseObject ? asset.warehouseType : 'snowflake',
    selectedProjectId ?? 0,
    selectedEnvironmentId ?? 0,
  );

  const openCanvasLink = (target: CanvasHandoff) => {
    if (!dwhHandoff) return;

    sendHandoffEventAccount({
      handoff_to: 'canvas',
      is_final_handoff_action: true,
      handoff_action: target,
      asset_type: toHandoffAssetDataWarehouseType(asset.type),
    });
    if (target === 'create') {
      // eslint-disable-next-line react-compiler/react-compiler
      window.location.href = dwhHandoff.canvasCreateLink;
    } else if (target === 'edit') {
      window.location.href = dwhHandoff.canvasEditLink;
    }
    setIsOpen(false);
  };

  const actions = [
    {
      text: 'Open',
      type: 'secondary' as const,
      onClick: () => openCanvasLink('create'),
      buttonProps: {
        isDisabled: isDataWarehouseObject && !selectedEnvironmentId,
      },
    },
  ];

  return (
    <Modal
      title="Open in Canvas"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      modalType="success"
      overflow="visible"
      actions={actions}
    >
      {isDataWarehouseObject && (
        <ProjectEnvironmentSelector
          projectOptions={projectOptions}
          environmentOptions={environmentOptions}
          selectedProjectOption={selectedProjectOption}
          selectedEnvironmentOption={selectedEnvironmentOption}
          isLoading={isLoading}
          isLoadingEnvironments={isLoadingEnvironments}
          onProjectChange={handleProjectChange}
          onEnvironmentChange={handleEnvironmentChange}
        />
      )}
    </Modal>
  );
};
